import { useCallback, useMemo } from "react";
import { AssessmentTab, AssessmentLayer } from "./types";
import { SiteReportLoadingState } from "./SiteReportContent";
import { useCheckboxHierarchy } from "./hooks/useCheckboxHierarchy";
import { useFilteredAssessmentTabs } from "./hooks/useFilteredAssessmentTabs";
import { useLayerActivation } from "./hooks/useLayerActivation";
import { useReportErrorHandler } from "./hooks/useReportErrorHandler";
import { LayerBasedToggleStrategy, SectionBasedToggleStrategy } from "./strategies/ToggleStrategy";

export interface AssessmentStateConfig {
  siteId: string;
  supportsSectionIds: boolean;
  featureFlags?: {
    nearbyTab?: boolean;
  };
}

export interface AssessmentStateReturn {
  assessmentState: Record<string, Record<string, boolean>>;
  toggleAssessmentState: (params: { tabKey: string; layerId?: string; groupId?: string }) => void;
  activeAssessmentLayers: AssessmentLayer[];
  reportLoadedHandle: (hasLoaded: boolean, loadingState: SiteReportLoadingState) => void;
  checkboxState: ReturnType<typeof useCheckboxHierarchy>;
  assessmentTabs: AssessmentTab[];
  reportLoadingState: ReturnType<typeof useReportErrorHandler>["reportLoadingState"];
}

export function useAssessmentStateManager(config: AssessmentStateConfig): AssessmentStateReturn {
  const { siteId, supportsSectionIds, featureFlags } = config;

  const assessmentTabs = useFilteredAssessmentTabs(featureFlags);

  const toggleStrategy = useMemo(
    () => (supportsSectionIds ? new SectionBasedToggleStrategy() : new LayerBasedToggleStrategy()),
    [supportsSectionIds]
  );

  const allLayerIds = useMemo(() => {
    return toggleStrategy.buildPaths(assessmentTabs);
  }, [assessmentTabs, toggleStrategy]);

  const checkboxState = useCheckboxHierarchy(siteId, allLayerIds);

  const { activeLayers } = useLayerActivation(assessmentTabs, checkboxState, supportsSectionIds);

  const assessmentState = useMemo(() => {
    const state: Record<string, Record<string, boolean>> = {};

    assessmentTabs.forEach((tab) => {
      state[tab.key] = {};
      tab.groups.forEach((group) => {
        if (supportsSectionIds && group.sectionIds) {
          group.sectionIds.forEach((sectionId) => {
            const path = `${tab.key}.${group.id}.${sectionId}`;
            state[tab.key][sectionId] = checkboxState.getCheckboxState(path);
          });
        } else {
          group.layerIds.forEach((layerId) => {
            const path = `${tab.key}.${layerId}`;
            state[tab.key][layerId] = checkboxState.getCheckboxState(path);
          });
        }
      });
    });

    return state;
  }, [assessmentTabs, checkboxState, supportsSectionIds]);

  const toggleAssessmentState = useCallback(
    (params: { tabKey: string; layerId?: string; groupId?: string }) => {
      toggleStrategy.toggle(params, assessmentTabs, checkboxState, allLayerIds);
    },
    [toggleStrategy, assessmentTabs, checkboxState, allLayerIds]
  );

  const { reportLoadingState, handleReportLoaded } = useReportErrorHandler();

  const reportLoadedHandle = useCallback(
    (hasLoaded: boolean, loadingState: SiteReportLoadingState) => {
      handleReportLoaded(hasLoaded, loadingState, assessmentState, assessmentTabs, toggleAssessmentState);
    },
    [handleReportLoaded, assessmentState, assessmentTabs, toggleAssessmentState]
  );

  return {
    assessmentState,
    toggleAssessmentState,
    activeAssessmentLayers: activeLayers,
    reportLoadedHandle,
    checkboxState,
    assessmentTabs,
    reportLoadingState,
  };
}
